import "./PaymentTermsV2.scss";
import React from "react";
import {observer} from "mobx-react-lite";
import {IconButton, Skeleton, Stack, Tooltip, Typography} from "@mui/material";
import {SettingsOutlined} from "@mui/icons-material";
import {generatePath, Link as RouterLink, Navigate, Outlet, useLocation} from "react-router";
import {MainPageFacade} from "~/components/main/page/MainPageFacade.tsx";
import {CARD_CONFIG} from "~/components/dashboard/DashboardCardConfigs.tsx";
import {useStores} from "~/stores";
import {routes} from "~/routing/routes.ts";
import {
    PT_V2_TABS,
    type PtV2OutletContext,
    visiblePtV2Tabs,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {PtV2TabKey} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {
    usePaymentTermsV2DatabagId,
    usePtV2SettingsQuery,
    usePtV2SummaryQuery,
} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";

/** Page shell: the title, then the active tab (KPIs, tabs, summary, content). Data management lives on Data sources. */
export const PaymentTermsV2Page: React.FC = observer(() => {
    const {authStore, p} = useStores();
    const databagId = usePaymentTermsV2DatabagId();
    const summaryQ = usePtV2SummaryQuery();
    const settingsQ = usePtV2SettingsQuery();
    const {pathname} = useLocation();
    const isStaff = authStore.isMithraStaffGroup;
    const base = generatePath(routes.payment_terms_v2, {id: String(databagId ?? '')});

    const context: PtV2OutletContext = {
        summary: summaryQ.data,
        summaryLoading: summaryQ.isPending,
        summaryError: summaryQ.isError,
        hasData: Boolean(summaryQ.data?.lastImport),
        isStaff,
        tabPath: (tab: PtV2TabKey) => `${base}/${tab}`,
        tabs: visiblePtV2Tabs(isStaff, settingsQ.data),
        costOfCapitalPct: settingsQ.data?.cost_of_capital_pct ?? null,
    };

    // A link to a tab this user does not see (hidden by staff, or staff-only) opens Overview instead. A customer waits
    // for the settings first: nothing but Overview shows until they say which tabs are hidden.
    const current = PT_V2_TABS.find(t => pathname.endsWith(`/${t.key}`))?.key;
    const waiting = !isStaff && settingsQ.isPending && current !== undefined && current !== 'overview';
    const blocked = !waiting && current && current !== 'overview' && !context.tabs.some(t => t.key === current);

    const card = CARD_CONFIG['payment-terms-v2'];
    return <MainPageFacade
        title={<Stack direction="row" alignItems="center" gap={1}>
            <Typography component="h1" variant="h5" fontWeight={700}>{card.title}</Typography>
            {/* Mithra staff only, like the settings page itself. */}
            {isStaff && <Tooltip title="Payment Terms settings">
                <IconButton size="small" aria-label="Payment Terms settings" component={RouterLink} to={`${base}/settings`}>
                    <SettingsOutlined fontSize="small"/>
                </IconButton>
            </Tooltip>}
        </Stack>}
        titleTag={p.cardTag('payment-terms-v2')}
        className="payment-terms-v2-page">
        {waiting ? <Skeleton variant="rounded" height={300}/>
            : blocked ? <Navigate to={context.tabPath('overview')} replace/> : <Outlet context={context}/>}
    </MainPageFacade>;
});
